import React, { useState, useMemo } from 'react';
import {
  CheckCircle, Clock, CalendarBlank, TrendUp, CurrencyDollar,
  Leaf, Factory, Lightning
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell
} from 'recharts';

const STATUS_CONFIG = {
  Completed: { color: '#4ade80', icon: CheckCircle, bg: 'bg-emerald-500/10' },
  'In Progress': { color: '#facc15', icon: Clock, bg: 'bg-amber-500/10' },
  Planned: { color: '#60a5fa', icon: CalendarBlank, bg: 'bg-blue-500/10' },
};

const CATEGORY_COLORS = {
  Energy: '#facc15',
  Utilities: '#60a5fa',
  Maintenance: '#94a3b8',
  Quality: '#f97316',
  Water: '#38bdf8',
  Operations: '#2dd4bf',
  Safety: '#c084fc',
  'Supply Chain': '#fb923c',
};

function fmtVnd(n) {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n?.toLocaleString?.() ?? n;
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-[#1a1a1a] border border-border rounded-lg p-3 shadow-lg text-xs font-body max-w-xs">
      <p className="font-semibold text-text-primary mb-1">{d.title}</p>
      <p className="text-text-secondary">VND saved/day: <span className="text-emerald-400 font-medium">{fmtVnd(d.vnd_saved_day)}</span></p>
      <p className="text-text-secondary">OEE points: <span className="text-amber-400 font-medium">+{d.oee_points_lifted}</span></p>
      <p className="text-text-secondary">CO2e reduced: <span className="text-teal-400 font-medium">{d.co2e_reduced_day_kg} kg/day</span></p>
    </div>
  );
}

export default function OeeInitiatives({ data }) {
  const initiatives = data?.initiatives || [];
  const summary = data?.initiative_summary || {};
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('vnd');

  const filtered = useMemo(() => {
    let d = initiatives;
    if (statusFilter !== 'all') d = d.filter(i => i.status === statusFilter);
    return [...d].sort((a, b) => {
      if (sortBy === 'vnd') return b.vnd_saved_day - a.vnd_saved_day;
      if (sortBy === 'oee') return b.oee_points_lifted - a.oee_points_lifted;
      if (sortBy === 'co2') return b.co2e_reduced_day_kg - a.co2e_reduced_day_kg;
      return 0;
    });
  }, [initiatives, statusFilter, sortBy]);

  const leaderboard = initiatives.slice(0, 5);

  return (
    <div className="space-y-5 animate-fade-in" data-testid="oee-initiatives">
      {/* Summary Bar */}
      <div className="bg-surface border border-border rounded-lg p-4 flex flex-wrap items-center gap-6">
        <div className="text-center">
          <p className="font-heading text-2xl font-bold text-text-primary">{summary.total || 0}</p>
          <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">Total</p>
        </div>
        {['Completed', 'In Progress', 'Planned'].map(s => {
          const cfg = STATUS_CONFIG[s];
          return (
            <div key={s} className="text-center">
              <p className="font-heading text-2xl font-bold" style={{ color: cfg.color }}>
                {s === 'Completed' ? summary.completed : s === 'In Progress' ? summary.in_progress : summary.planned}
              </p>
              <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">{s}</p>
            </div>
          );
        })}
        <div className="border-l border-border pl-6 text-center">
          <p className="font-heading text-2xl font-bold text-emerald-400">{fmtVnd(summary.total_vnd_saved_day || 0)}</p>
          <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">VND saved/day</p>
        </div>
        <div className="text-center">
          <p className="font-heading text-2xl font-bold text-teal-400">{summary.total_co2e_reduced_day_kg || 0} kg</p>
          <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">CO2e reduced/day</p>
        </div>
      </div>

      {/* Leaderboard Chart */}
      <div className="bg-surface border border-border rounded-lg p-5">
        <h2 className="font-heading text-base font-semibold text-text-primary mb-4">Top 5 Initiatives by VND Savings</h2>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={leaderboard} layout="vertical" margin={{ left: 10, right: 30 }}>
              <XAxis type="number" tick={{ fill: '#A3A380', fontSize: 11 }} tickFormatter={v => fmtVnd(v)} />
              <YAxis dataKey="init_id" type="category" width={70} tick={{ fill: '#A3A380', fontSize: 11 }} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="vnd_saved_day" radius={[0, 4, 4, 0]} barSize={18}>
                {leaderboard.map((entry, idx) => (
                  <Cell key={idx} fill={CATEGORY_COLORS[entry.category] || '#6b7280'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs font-body font-semibold text-text-secondary uppercase tracking-wider">Status</label>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-testid="oee-init-filter-status"
            className="bg-background border border-border rounded-md px-3 py-1.5 text-sm font-body text-text-primary focus:ring-1 focus:ring-primary">
            <option value="all">All</option>
            <option value="Completed">Completed</option>
            <option value="In Progress">In Progress</option>
            <option value="Planned">Planned</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs font-body font-semibold text-text-secondary uppercase tracking-wider">Sort</label>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} data-testid="oee-init-sort"
            className="bg-background border border-border rounded-md px-3 py-1.5 text-sm font-body text-text-primary focus:ring-1 focus:ring-primary">
            <option value="vnd">VND Saved</option>
            <option value="oee">OEE Impact</option>
            <option value="co2">CO2e Reduced</option>
          </select>
        </div>
      </div>

      {/* Initiative Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filtered.map((init) => {
          const cfg = STATUS_CONFIG[init.status] || STATUS_CONFIG.Planned;
          const Icon = cfg.icon;
          const realizationPct = init.predicted_saving_vnd_month > 0
            ? Math.round(init.realized_saving_vnd_month / init.predicted_saving_vnd_month * 100) : 0;
          return (
            <div key={init.init_id} className="bg-surface border border-border rounded-lg p-4 hover:border-tertiary/40 transition-all" data-testid={`oee-init-${init.init_id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-lg ${cfg.bg} flex items-center justify-center shrink-0`}>
                  <Icon size={16} weight="fill" style={{ color: cfg.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="font-heading text-sm font-semibold text-text-primary truncate">{init.init_id}: {init.title}</p>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-body font-medium shrink-0" style={{ backgroundColor: cfg.color + '20', color: cfg.color }}>
                      {init.status}
                    </span>
                  </div>
                  <p className="text-xs font-body text-text-secondary mb-3">{init.description}</p>

                  <div className="grid grid-cols-3 gap-2 mb-3 text-center">
                    <div className="bg-background rounded-md p-2">
                      <p className="font-heading text-sm font-bold text-emerald-400">+{init.oee_points_lifted}</p>
                      <p className="text-[9px] font-body text-text-secondary">OEE pp</p>
                    </div>
                    <div className="bg-background rounded-md p-2">
                      <p className="font-heading text-sm font-bold text-amber-400">{fmtVnd(init.vnd_saved_day)}</p>
                      <p className="text-[9px] font-body text-text-secondary">VND/day</p>
                    </div>
                    <div className="bg-background rounded-md p-2">
                      <p className="font-heading text-sm font-bold text-teal-400">{init.co2e_reduced_day_kg}kg</p>
                      <p className="text-[9px] font-body text-text-secondary">CO2e/day</p>
                    </div>
                  </div>

                  {/* Savings validation */}
                  <div className="mb-2">
                    <div className="flex items-center justify-between text-[10px] font-body mb-1">
                      <span className="text-text-secondary">Savings Validation</span>
                      <span className="font-medium" style={{ color: realizationPct >= 80 ? '#4ade80' : realizationPct >= 50 ? '#facc15' : '#ef4444' }}>
                        {realizationPct}% realized
                      </span>
                    </div>
                    <div className="h-1.5 bg-background rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{
                        width: `${Math.min(realizationPct, 100)}%`,
                        backgroundColor: realizationPct >= 80 ? '#4ade80' : realizationPct >= 50 ? '#facc15' : '#ef4444',
                      }} />
                    </div>
                    <p className="text-[9px] font-body text-text-secondary mt-1">{init.validation_status}</p>
                  </div>

                  <div className="flex flex-wrap gap-3 text-[10px] font-body text-text-secondary">
                    <span>Owner: {init.owner}</span>
                    <span>Due: {init.due_date}</span>
                    <span>Lines: {init.lines_affected?.join(', ')}</span>
                    <span className="text-teal-400">ESG: {init.esg_alignment}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
